import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import SocialShareModalProps, { SocialShareButton } from "./props";
import Modal from '../../private/Modal';
import ModalProps from '../../private/Modal/props';
import Icon from '../../private/Icon';
import Content from '../../private/Content';
import './styles.scss';

import copy from '../../../icons/copy-link.svg';
import print from '../../../icons/print-2.svg';
import email from '../../../icons/email-2.svg';
import facebook from '../../../icons/facebook-3.svg';
import x from '../../../icons/x-2.svg';
import whatsapp from '../../../icons/whatsapp-2.svg';

const icons: { [key: string]: string } = {
  x,
  copy,
  print,
  email,
  facebook,
  whatsapp,
}

function SocialShareModal(props: SocialShareModalProps) {
  const [responseMessage, setResponseMessage] = useState<{ [key: string]: boolean }>({});
  const timeoutsRef = useRef<{ [key: string]: NodeJS.Timeout }>({});
  
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
    };
  }, []);
  
  const handleItemClick = useSocialShareClick(setResponseMessage, timeoutsRef, props?.responseDelay);
  
  const modalProps = useModalProps(props);

  return (
    <div className={`social-share-modal`}>
      <Modal { ...modalProps }>
        <div className="social-share__container">
          <div className="social-share__title">{ props.title }</div>
          <div className="social-share-content">
            { props.buttons.map((button: SocialShareButton, index: number) => (
              <div key={index} className="social-share-content__item" onClick={handleItemClick(button)} >
                <div className="social-share-content__text">
                  <Content text={button.text} />
                  {button.responseText && responseMessage[button.icon] && <Content text={button.responseText} />}
                </div>
                <Icon svg={icons[button.icon]} alt={button.text} />
              </div>
            )) }
          </div>
        </div>
      </Modal>
    </div>
  );
}

const useSocialShareClick = (
  setResponseMessage: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>,
  timeoutsRef: React.MutableRefObject<{ [key: string]: NodeJS.Timeout }>,
  responseDelay: number | undefined = 4000
) => useCallback((button: SocialShareButton) => (e: React.MouseEvent<HTMLElement>) => {
  button?.onClick && button.onClick(e, button.icon);
  if (!button?.responseText) {
    return;
  }
  if (timeoutsRef.current[button.icon]) {
    clearTimeout(timeoutsRef.current[button.icon]);
  }
  
  setResponseMessage((prev) => ({
    ...prev,
    [button.icon]: true
  }));

  const timeoutId = setTimeout(() => {
    setResponseMessage((prev) => ({
      ...prev,
      [button.icon]: false
    }));
    delete timeoutsRef.current[button.icon];
  }, responseDelay);
  
  timeoutsRef.current[button.icon] = timeoutId;
  
}, [setResponseMessage, timeoutsRef, responseDelay]);

const useModalProps = (props: SocialShareModalProps): ModalProps => useMemo(() => ({
  Button: {
    onClick: props?.onClose
  },
  Blocker: {
    onClick: props?.onClose,
    classNames: {
      scroll: true,
      darkOverlay: true
    }
  },
  width: 375,
  classNames: {
    disableRadiusDesktop: true,
    mobileMediumRadius: false
  }
}), [props])

export default React.memo(SocialShareModal);